import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import { useSiteDataApi } from "../../utilities/SiteDataProvider";

const SOCIAL_ASSISTANCE_CODES = [
  "",
  "OWP",
  "ODS",
  "ACS",
  "CCA",
  "LTC",
  "SEN",
  "REG",
];
const PLACEMENT_OPTIONS = [
  { value: "L", label: "Left" },
  { value: "R", label: "Right" },
];

const DEVICE_CODE_OPTIONS = [
  { code: "HA0000939", label: "Behind the Ear" },
  { code: "HA0000940", label: "Canal Aid" },
  { code: "HA0000941", label: "Completely in the Canal" },
  { code: "HA0000942", label: "In the Ear" },
];

/**
 * Increment an invoice number's letter suffix.
 * "10001" → "10001-A"
 * "10001-A" → "10001-B"
 * "10001-Z" → "10001-AA"
 * "10001-AA" → "10001-AB"
 */
function incrementInvoiceNumber(invoiceNumber) {
  const parts = invoiceNumber.split("-");
  if (parts.length === 1) {
    // No letter suffix yet → add -A
    return invoiceNumber + "-A";
  }
  const base = parts.slice(0, -1).join("-");
  const suffix = parts[parts.length - 1];

  // Check if suffix is purely alpha
  if (/^[A-Z]+$/i.test(suffix)) {
    const incremented = incrementLetters(suffix.toUpperCase());
    return base + "-" + incremented;
  }
  // If suffix is not a letter (e.g. numeric), append -A
  return invoiceNumber + "-A";
}

function incrementLetters(str) {
  let carry = true;
  let result = str.split("");
  for (let i = result.length - 1; i >= 0 && carry; i--) {
    let code = result[i].charCodeAt(0) + 1;
    if (code > 90) {
      // Z → A with carry
      result[i] = "A";
      carry = true;
    } else {
      result[i] = String.fromCharCode(code);
      carry = false;
    }
  }
  if (carry) {
    result.unshift("A");
  }
  return result.join("");
}

/**
 * Format a date string to DDMMYYYY for the TXT file.
 * Handles various input formats.
 */
function formatDateDDMMYYYY(dateStr) {
  if (!dateStr) return "";
  // Already in DDMMYYYY format (8 digits)?
  if (/^\d{8}$/.test(dateStr)) return dateStr;
  // YYYY-MM-DD from date input — use string manipulation to avoid timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [yyyy, mm, dd] = dateStr.split("-");
    return dd + mm + yyyy;
  }
  // Fallback: parse as date using UTC methods to avoid timezone issues
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return dd + mm + yyyy;
}

/**
 * Format a date for display in the form (YYYY-MM-DD)
 */
function formatDateForInput(dateStr) {
  if (!dateStr) return "";
  // If already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // If DDMMYYYY
  if (/^\d{8}$/.test(dateStr)) {
    return (
      dateStr.substring(4, 8) +
      "-" +
      dateStr.substring(2, 4) +
      "-" +
      dateStr.substring(0, 2)
    );
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toISOString().substring(0, 10);
}

/**
 * Format currency value to 2 decimal places
 */
function formatCurrency(val) {
  if (val === "" || val === null || val === undefined) return "0.00";
  const num = parseFloat(val);
  if (isNaN(num)) return "0.00";
  return num.toFixed(2);
}

/**
 * Build a single CSV row from field values
 */
function buildCSVRow(fields) {
  return [
    fields.vendorNo,
    fields.claimNo,
    fields.healthcardFour,
    fields.invoiceNumber,
    formatDateDDMMYYYY(fields.invoiceDate),
    formatDateDDMMYYYY(fields.deliveryDate),
    formatDateDDMMYYYY(fields.serviceStartDate),
    formatDateDDMMYYYY(fields.serviceEndDate),
    fields.deviceCode,
    fields.serialNumber,
    fields.placement,
    fields.quantity,
    formatCurrency(fields.unitPrice),
    formatCurrency(fields.adpPortion),
    formatCurrency(fields.clientPortion),
    fields.socialAssistanceCode,
  ].join(",");
}

/**
 * Build the full TXT file content.
 * For paid invoices: return rows (negative) + new rows (corrected, new invoice #)
 * For unpaid/on-hold: just corrected rows with same invoice #
 */
function buildTXTContent(originalRows, editedRows, isPaid) {
  if (!isPaid) {
    // On-hold: just the corrected rows
    return editedRows.map((row) => buildCSVRow(row)).join("\n");
  }

  // Paid: return rows (negative) + corrected rows (new invoice number)
  const returnRows = originalRows.map((row) => buildCSVRow(row));
  const correctedRows = editedRows.map((row) => buildCSVRow(row));
  return [...returnRows, ...correctedRows].join("\n");
}

export default function EditModal({ open, editDetails, handleClose, isPaid }) {
  const [siteDataContext] = useSiteDataApi();
  const [modalStep, setModalStep] = useState("loading"); // loading, form, confirm, submitting, success, error
  const [originalRows, setOriginalRows] = useState([]); // parsed from XML, with negative values for returns
  const [editedFields, setEditedFields] = useState([]); // array of editable row objects
  const [newInvoiceNumber, setNewInvoiceNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (open && editDetails) {
      setModalStep("loading");
      setOriginalRows([]);
      setEditedFields([]);
      setErrorMessage("");
      setSuccessMessage("");
      fetchEditData();
    }
  }, [open, editDetails]);

  const fetchEditData = async () => {
    try {
      const res = await fetch("/api/ADPEditParse", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": siteDataContext.thhcsfrtoken,
        },
        body: JSON.stringify({
          patient: editDetails.patient,
          healthcard_four: editDetails.healthcard_four,
          invoice_number: editDetails.invoice_number,
          adp_invoice_number: editDetails.adp_invoice_number,
          adp_claim_number: editDetails.adp_claim_number,
          isPaid,
        }),
      });

      const data = await res.json();

      if (data.error) {
        setErrorMessage(data.error);
        setModalStep("error");
        return;
      }

      const { rows, returnRows } = data;

      // returnRows have negative values (for paid invoices)
      if (isPaid && returnRows) {
        setOriginalRows(returnRows);
      }

      // Calculate new invoice number for paid edits
      // Use the ADP-accepted invoice number (from _adp_status) as the base,
      // NOT the Blueprint invoice number which may differ
      const currentInvoice =
        rows[0]?.invoiceNumber ||
        editDetails.adp_invoice_number ||
        editDetails.invoice_number;
      const nextInvoice = isPaid
        ? incrementInvoiceNumber(currentInvoice)
        : currentInvoice;
      setNewInvoiceNumber(nextInvoice);

      // Pre-populate editable fields from parsed rows
      const editable = rows.map((row) => ({
        ...row,
        invoiceNumber: isPaid ? nextInvoice : row.invoiceNumber,
        // Convert dates to input-friendly format
        invoiceDate: formatDateForInput(row.invoiceDate),
        deliveryDate: formatDateForInput(row.deliveryDate),
        serviceStartDate: formatDateForInput(row.serviceStartDate),
        serviceEndDate: formatDateForInput(row.serviceEndDate),
      }));

      setEditedFields(editable);
      setModalStep("form");
    } catch (error) {
      console.log("error fetching edit data", error);
      setErrorMessage("Error loading invoice data: " + error.message);
      setModalStep("error");
    }
  };

  const handleFieldChange = (rowIndex, fieldName, value) => {
    setEditedFields((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [fieldName]: value };

      // If invoice number changes, update all rows
      if (fieldName === "invoiceNumber") {
        updated.forEach((row, i) => {
          updated[i] = { ...updated[i], invoiceNumber: value };
        });
        setNewInvoiceNumber(value);
      }

      // Auto-calculate unit price when ADP or client portion changes
      if (fieldName === "adpPortion" || fieldName === "clientPortion") {
        const adp = parseFloat(updated[rowIndex].adpPortion) || 0;
        const client = parseFloat(updated[rowIndex].clientPortion) || 0;
        updated[rowIndex].unitPrice = (adp + client).toFixed(2);
      }

      return updated;
    });
  };

  const handleConfirm = () => {
    setModalStep("confirm");
  };

  const handleSubmit = async () => {
    setModalStep("submitting");
    try {
      // Build the final rows for submission
      const finalEditedRows = editedFields.map((row) => ({
        ...row,
        invoiceDate: formatDateDDMMYYYY(row.invoiceDate),
        deliveryDate: formatDateDDMMYYYY(row.deliveryDate),
        serviceStartDate: formatDateDDMMYYYY(row.serviceStartDate),
        serviceEndDate: formatDateDDMMYYYY(row.serviceEndDate),
      }));

      const txtContent = buildTXTContent(originalRows, finalEditedRows, isPaid);

      const res = await fetch("/api/ADPProcessEdit", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": siteDataContext.thhcsfrtoken,
        },
        body: JSON.stringify({
          content: txtContent,
          patientName: editDetails.patient,
          originalInvoiceNumber:
            editDetails.adp_invoice_number || editDetails.invoice_number,
          newInvoiceNumber: isPaid
            ? newInvoiceNumber
            : editDetails.adp_invoice_number || editDetails.invoice_number,
          healthcardFour: editDetails.healthcard_four,
          isPaid,
          editedFields: editedFields,
          originalRows: isPaid ? originalRows : undefined,
        }),
      });

      const data = await res.json();

      if (data.error) {
        setErrorMessage(data.error);
        setModalStep("error");
        return;
      }

      setSuccessMessage(data.message || data);
      setModalStep("success");
    } catch (error) {
      console.log("error submitting edit", error);
      setErrorMessage("Error submitting edit: " + error.message);
      setModalStep("error");
    }
  };

  const getTxtPreview = () => {
    try {
      const finalEditedRows = editedFields.map((row) => ({
        ...row,
        invoiceDate: formatDateDDMMYYYY(row.invoiceDate),
        deliveryDate: formatDateDDMMYYYY(row.deliveryDate),
        serviceStartDate: formatDateDDMMYYYY(row.serviceStartDate),
        serviceEndDate: formatDateDDMMYYYY(row.serviceEndDate),
      }));
      return buildTXTContent(originalRows, finalEditedRows, isPaid);
    } catch {
      return "Error generating preview";
    }
  };

  const renderForm = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Edit Invoice for {editDetails.patient}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {isPaid
            ? "This is a paid invoice. A return (negative values) will be submitted along with corrected values using a new invoice number."
            : "This invoice is outstanding. The corrected values will replace the existing submission."}
        </Typography>

        {isPaid && (
          <Alert severity="info" sx={{ mb: 2 }}>
            ADP invoice being returned:{" "}
            <strong>
              {editDetails.adp_invoice_number || editDetails.invoice_number}
            </strong>{" "}
            → New invoice: <strong>{newInvoiceNumber}</strong>
            {editDetails.adp_invoice_number &&
              editDetails.adp_invoice_number !== editDetails.invoice_number && (
                <>
                  <br />
                  <em>
                    Note: Blueprint invoice #{editDetails.invoice_number}{" "}
                    differs from ADP invoice #{editDetails.adp_invoice_number}
                  </em>
                </>
              )}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {editedFields.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 2,
              mb: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Line {rowIndex + 1} —{" "}
              {row.placement === "L"
                ? "Left"
                : row.placement === "R"
                  ? "Right"
                  : row.placement || "N/A"}{" "}
              ear
            </Typography>
            <Grid container spacing={2}>
              {/* Read-only fields */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Vendor #"
                  value={row.vendorNo}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Claim #"
                  value={row.claimNo}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Health Card (last 4)"
                  value={row.healthcardFour}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>

              {/* Editable fields */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Invoice #"
                  value={row.invoiceNumber}
                  fullWidth
                  size="small"
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "invoiceNumber", e.target.value)
                  }
                  helperText={isPaid ? "New invoice # for the correction" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Invoice Date"
                  type="date"
                  value={row.invoiceDate}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "invoiceDate", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Delivery Date"
                  type="date"
                  value={row.deliveryDate}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "deliveryDate", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Device Code"
                  value={row.deviceCode}
                  fullWidth
                  size="small"
                  select
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "deviceCode", e.target.value)
                  }
                >
                  {DEVICE_CODE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.code} value={opt.code}>
                      {opt.label} ({opt.code})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Serial Number"
                  value={row.serialNumber}
                  fullWidth
                  size="small"
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "serialNumber", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Placement"
                  value={row.placement}
                  fullWidth
                  size="small"
                  select
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "placement", e.target.value)
                  }
                >
                  {PLACEMENT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="ADP Portion"
                  value={row.adpPortion}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "adpPortion", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Client Portion"
                  value={row.clientPortion}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  onChange={(e) =>
                    handleFieldChange(rowIndex, "clientPortion", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Social Assistance Program Code"
                  value={row.socialAssistanceCode || ""}
                  fullWidth
                  size="small"
                  select
                  onChange={(e) =>
                    handleFieldChange(
                      rowIndex,
                      "socialAssistanceCode",
                      e.target.value,
                    )
                  }
                >
                  {SOCIAL_ASSISTANCE_CODES.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code || "(None)"}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        {/* Real-time TXT preview */}
        <Typography variant="subtitle1" gutterBottom>
          TXT File Preview
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This is the content that will be submitted to ADP
        </Typography>
        <Box
          sx={{
            border: "1px solid #000",
            borderRadius: 1,
            p: 2,
            mt: 1,
            mb: 2,
            backgroundColor: "#f5f5f5",
            fontFamily: "monospace",
            fontSize: "12px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: "200px",
            overflow: "auto",
          }}
        >
          {getTxtPreview()}
        </Box>

        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button onClick={handleClose} variant="outlined" color="primary">
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={handleConfirm}>
              Review & Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderConfirmation = () => {
    const preview = getTxtPreview();
    const vendorNo = editedFields[0]?.vendorNo || "";
    const txtFileName = `${vendorNo}_<server-date>.txt`;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Confirm Edit Submission
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The following TXT file will be uploaded to ADP via EDT. Please review
          carefully.
        </Alert>
        <Typography variant="body2" gutterBottom>
          <strong>File name:</strong> {txtFileName}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Patient:</strong> {editDetails.patient}
        </Typography>
        {isPaid && (
          <>
            <Typography variant="body2" gutterBottom>
              <strong>Return invoice:</strong> {editDetails.invoice_number}{" "}
              (negative values)
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>New invoice:</strong> {newInvoiceNumber} (corrected
              values)
            </Typography>
          </>
        )}
        <Box
          sx={{
            border: "1px solid #000",
            borderRadius: 1,
            p: 2,
            mt: 1,
            mb: 3,
            backgroundColor: "#f5f5f5",
            fontFamily: "monospace",
            fontSize: "12px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {preview}
        </Box>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button onClick={() => setModalStep("form")} variant="outlined">
              Back to Edit
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => {
                const blob = new Blob([preview], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = txtFileName;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download TXT
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={handleClose} variant="outlined" color="primary">
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="error" onClick={handleSubmit}>
              Submit to ADP
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="edit-modal-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: "1100px",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
        }}
      >
        {modalStep === "loading" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading invoice data...</Typography>
          </Box>
        )}

        {modalStep === "form" && renderForm()}

        {modalStep === "confirm" && renderConfirmation()}

        {modalStep === "submitting" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Submitting edit to ADP...</Typography>
          </Box>
        )}

        {modalStep === "success" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {typeof successMessage === "string"
                ? successMessage
                : "Edit submitted successfully!"}
            </Alert>
            <Button onClick={handleClose} variant="outlined" color="primary">
              Close
            </Button>
          </Box>
        )}

        {modalStep === "error" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "An error occurred"}
            </Alert>
            <Button onClick={handleClose} variant="outlined" color="primary">
              Close
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
